import { NavLink } from 'react-router-dom';
import {
  Award,
  Calendar,
  Heart,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Map,
  MessageCircle,
  Settings,
  Star,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { GlassCard } from '@/components/shared/GlassCard';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { cn } from '@/lib/utils/cn';

type Item = { to: string; label: string; icon: LucideIcon; badge?: string; end?: boolean };

const ITEMS: Item[] = [
  { to: '/dashboard',            label: 'Overview',    icon: LayoutDashboard, end: true },
  { to: '/dashboard/bookings',   label: 'Bookings',    icon: Calendar,        badge: '2' },
  { to: '/dashboard/wishlists',  label: 'Wishlists',   icon: Heart,           badge: '23' },
  { to: '/dashboard/reviews',    label: 'Reviews',     icon: Star,            badge: '12' },
  { to: '/dashboard/rewards',    label: 'Rewards',     icon: Award,           badge: '1,240' },
  { to: '/dashboard/community',  label: 'Companions',  icon: Users },
  { to: '/dashboard/trip-planner', label: 'Trip Planner', icon: Map },
  { to: '/dashboard/messages',   label: 'Messages',    icon: MessageCircle,   badge: '2' },
  { to: '/dashboard/settings',   label: 'Settings',    icon: Settings },
];

export function DashboardSidebar() {
  return (
    <GlassCard className="flex flex-col p-3 lg:sticky lg:top-24 lg:h-[calc(100vh-7rem)]">
      {/* Profile header */}
      <div className="flex items-center gap-3 px-2 py-2.5">
        <img
          src={unsplash(photo.guideF2, { w: 100 })}
          alt=""
          aria-hidden
          className="h-10 w-10 shrink-0 rounded-full object-cover"
          style={{
            border: `2px solid ${brand.teal}`,
            boxShadow: '0 0 16px var(--color-teal-glow)',
          }}
        />
        <div className="min-w-0">
          <div className="font-display text-sm font-semibold text-ink">Mehreen S.</div>
          <div className="text-[11px]" style={{ color: brand.teal }}>
            Adventurer · Tier 2
          </div>
        </div>
      </div>

      <hr className="my-2 border-[var(--glass-edge)]" />

      {/* Nav items */}
      <nav className="flex flex-col gap-0.5">
        {ITEMS.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] transition-colors',
                  isActive
                    ? 'font-semibold text-ink'
                    : 'text-ink-dim hover:bg-[var(--glass-hi)] hover:text-ink',
                )
              }
              style={({ isActive }) =>
                isActive
                  ? {
                      background: `linear-gradient(90deg, ${brand.teal}24, transparent)`,
                      borderLeft: `2px solid ${brand.teal}`,
                      paddingLeft: 10,
                    }
                  : undefined
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    size={15}
                    className="shrink-0"
                    color={isActive ? brand.teal : 'currentColor'}
                  />
                  <span className="flex-1">{it.label}</span>
                  {it.badge && (
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px] font-bold"
                      style={{
                        background: isActive ? `${brand.teal}24` : 'var(--glass-hi)',
                        color: isActive ? brand.teal : 'var(--ink-dim)',
                      }}
                    >
                      {it.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <hr className="my-2 border-[var(--glass-edge)]" />

      <NavLink
        to="/help"
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] text-ink-dim transition-colors hover:bg-[var(--glass-hi)] hover:text-ink"
      >
        <HelpCircle size={15} />
        Help center
      </NavLink>
      <button
        type="button"
        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[13px] transition-colors hover:bg-[var(--color-err)]/10"
        style={{ color: 'var(--color-err)' }}
      >
        <LogOut size={15} />
        Sign out
      </button>
    </GlassCard>
  );
}
